<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  entry: { type: Object, required: true },
});

const { t, tm, rt } = useI18n();

const labels = computed(() => ({
  contexts: tm('SALES.HISTORY.CONTEXT'),
  events: tm('SALES.EVENTS'),
  kinds: tm('SALES.HISTORY.KIND'),
}));

const resolveLabel = (messages, code) => {
  const message = messages?.[code];
  return message ? rt(message) : '';
};

const dateLabel = computed(() => {
  if (!props.entry.occurredAt) return '—';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(props.entry.occurredAt));
});
const actorTimeLabel = computed(() => {
  if (!props.entry.actorName) return dateLabel.value;

  return t('SALES.HISTORY.ACTOR_AT', {
    actor: props.entry.actorName,
    date: dateLabel.value,
  });
});
const contextLabel = computed(() =>
  props.entry.contextCode
    ? resolveLabel(labels.value.contexts, props.entry.contextCode)
    : ''
);
const eventLabel = computed(() =>
  resolveLabel(labels.value.events, props.entry.eventLabelCode)
);
const kindLabel = computed(() =>
  resolveLabel(labels.value.kinds, props.entry.kindCode)
);
</script>

<template>
  <li
    class="relative border-b border-lais-border-soft py-4 last:border-b-0"
    :data-test="`sales-history-item-${entry.id || entry.key}`"
  >
    <div class="flex min-w-0 gap-3">
      <span
        class="grid size-9 shrink-0 place-items-center rounded-full bg-lais-primary text-xs font-bold text-white"
        aria-hidden="true"
      >
        {{ entry.actorInitials }}
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap gap-2">
          <span
            v-if="entry.contextCode"
            class="rounded-full bg-lais-primary-50 px-2 py-1 text-xs font-semibold text-lais-primary"
          >
            {{ contextLabel }}
          </span>
          <span
            class="rounded-full bg-lais-bg-soft px-2 py-1 text-xs font-semibold text-lais-text-body"
          >
            {{ kindLabel }}
          </span>
        </div>
        <p class="mt-3 text-sm font-semibold text-lais-text">
          {{ eventLabel }}
        </p>
        <p v-if="entry.reason" class="mt-1 text-sm text-lais-text-body">
          {{ entry.reason }}
        </p>
        <p class="mt-2 text-xs text-lais-text-muted">
          {{ actorTimeLabel }}
        </p>
      </div>
    </div>
  </li>
</template>
